import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, LogOut } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useOrderNav } from "@/context/OrderNavContext";
import { useScrolling } from "@/lib/useScrolling";
import { BrandMark } from "@/components/BrandMark";


export const Navbar = () => {
  const { count, bumpKey } = useCart();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [bounce, setBounce] = useState(false);
  useEffect(() => {
    if (bumpKey > 0) { setBounce(true); const t = setTimeout(() => setBounce(false), 600); return () => clearTimeout(t); }
  }, [bumpKey]);
  const loc = useLocation();
  const scrolling = useScrolling();
  const orderNav = useOrderNav();

  const ORDER_ROUTES = ["/", "/shop", "/build-your-box", "/multipack-deals", "/guest-collection", "/economy-flavours", "/custom-order"];
  const handleBuildClick = (e) => {
    // If the OrderBuilder page is mounted, let it handle returning to the chooser
    // (with a reset warning if a build is in progress) instead of a plain navigation.
    if (orderNav?.current) { e.preventDefault(); orderNav.current(); }
  };

  const link = (to, label, testid) => {
    const active = loc.pathname === to;
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`text-xs font-mono uppercase tracking-[0.18em] transition-colors duration-200 hover:text-foreground ${active ? "text-foreground" : "text-muted-foreground"}`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className={`sticky top-0 z-50 border-b border-border bg-background/70 backdrop-blur-xl transition-transform duration-300 ${scrolling ? "-translate-y-full md:translate-y-0" : "translate-y-0"}`}>
      <div className="mx-auto flex h-24 max-w-[1400px] items-center justify-between px-6">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <BrandMark />
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          <Link
            to="/shop"
            data-testid="nav-shop"
            onClick={handleBuildClick}
            className={`text-xs font-mono uppercase tracking-[0.18em] transition-colors duration-200 hover:text-foreground ${ORDER_ROUTES.includes(loc.pathname) ? "text-foreground" : "text-muted-foreground"}`}
          >
            Order
          </Link>
          {link("/product-info", "Flavours", "nav-product-info")}
          {link("/how-to-order", "How to Order", "nav-how-to-order")}
          {link("/shipping", "Shipping", "nav-shipping")}
          {link("/track", "Track", "nav-track")}
          {user?.role === "dropship" && link("/trade", "Dashboard", "nav-trade-dashboard")}
          {user?.role === "admin" && link("/admin", "Admin", "nav-admin")}
        </nav>
        <div className="flex items-center gap-4">
          {user && (
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground">
              <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Logout</span>
            </button>
          )}
          <Link to="/cart" data-testid="nav-cart" className="relative flex items-center gap-2 bg-primary px-4 py-2 text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5">
            <motion.span animate={bounce ? { scale: [1, 1.5, 0.9, 1.15, 1], rotate: [0, -12, 10, -4, 0] } : { scale: 1 }} transition={{ duration: 0.6 }} className="inline-flex">
              <ShoppingBag className="h-4 w-4" />
            </motion.span>
            <span className="mono text-xs font-bold">{count}</span>
          </Link>
        </div>
      </div>
    </header>
  );
};
